import { useState, useEffect, useContext, createContext } from "react";

import { useKeycloak } from "@king-ict/eupisi-ui/hooks";

import { RoleAccess, UseProvidedAuthInterface } from "./types";
import {
  login as apiLogin,
  logout as apiLogout,
  roleChange as apiRoleChange,
  impersonate as apiImpersonate,
  unimpersonate as apiUnimpersonate,
} from "../../api/User/UserApi";
import { CommitteeTypeEnum } from "../../models/Committee";
import {
  User,
  UserInfo,
  UserPermission,
  CommitteeMember,
} from "../../models/User";

const authContext = createContext<UseProvidedAuthInterface>({} as any);

// =========== PROVIDER ===========

// Provider component that wraps your app and makes auth object ...
// ... available to any child component that calls useAuth().
export function ProvideAuth({ children }: { children: any }) {
  const auth = useProvideAuth();
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
}

// Hook for child components to get the auth object ...
// ... and re-render when it changes.
export const useAuth = () => {
  return useContext(authContext);
};

// =========== useAuth HOOK ===========

// Provider hook that creates auth object and handles state
function useProvideAuth(): any {
  const { keycloak } = useKeycloak();

  const [isLoading, setIsLoading] = useState(false);

  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState<User | undefined>();
  const [currentRole, setCurentRole] = useState<UserPermission | undefined>();
  const [roles, setRoles] = useState<Array<UserPermission>>([]);
  const [userInfo, setUserInfo] = useState<UserInfo | undefined>();

  useEffect(() => {
    let isAuthenticated = !!keycloak.authenticated;

    if (!isAuthenticated) {
      // If it is not authenticated clean user data
      setUserData();
    } else if (!authenticated) {
      // If is a new login fetch data from API
      checkLogin();
    }
    setUserInfo(keycloak.idTokenParsed as UserInfo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak]);

  const setUserData = (
    currentRole?: UserPermission,
    roles?: Array<UserPermission>
  ) => {
    setUser(currentRole ? currentRole.user : undefined);
    setCurentRole(currentRole);
    setRoles(roles || []);
    setAuthenticated(!!currentRole);
  };

  const login = (params?: any) => {
    if (keycloak.authenticated) {
      checkLogin();
    } else if (params) {
      keycloak.login(params);
    } else {
      keycloak.login();
    }
  };

  const checkLogin = () => {
    setIsLoading(true);

    let ret = new Promise(async (resolve, reject) => {
      try {
        // Login and just get a cookie
        await apiLogin();

        // Change role to get user data
        roleChange()
          .catch((y) => resolve(y))
          .catch((y) => reject(y));
      } catch (error) {
        console.log("[useAuth hook] Login ERROR !");
        console.log(error);
        logout();
        setIsLoading(false);
        alert(
          "Prijava nije moguća.\nNemate dodijeljenu korisničku ulogu u sustavu."
        );
        reject(error);
      }
    });

    return ret;
  };

  const logout = () => {
    setIsLoading(true);

    let ret = new Promise(async (resolve, reject) => {
      await apiLogout();
      keycloak.logout();

      setUserData();

      setIsLoading(true);
      resolve(true);
    });
    return ret;
  };

  const roleChange = (id?: number) => {
    setIsLoading(true);

    let ret = new Promise(async (resolve, reject) => {
      try {
        let response = await apiRoleChange(id);

        setUserData(response.currentUserRole, response.userRoles);
        sessionStorage.clear(); // If we changed roles, clear any storage data.

        resolve(response);
      } catch (error) {
        console.log("[useAuth hook] Change role ERROR !");
        console.log(error);
        logout();
        reject(error);
      }

      setIsLoading(false);
    });
    return ret;
  };

  const hasPermission = (permissionId: number) => {
    if (!currentRole || !currentRole.rolePermissions) return false;
    return currentRole.rolePermissions.includes(permissionId);
  };

  const impersonate = (id: number) => {
    setIsLoading(true);
    let ret = new Promise(async (resolve, reject) => {
      try {
        let response = await apiImpersonate(id);
        setUserData(response);
        roleChange();
        resolve(response);
      } catch (error) {
        console.log("[useAuth hook] Impersonate ERROR !");
        console.log(error);
        logout();
        reject(error);
      }
      setIsLoading(false);
    });
    return ret;
  };

  const unimpersonate = () => {
    setIsLoading(true);
    let ret = new Promise(async (resolve, reject) => {
      try {
        let response = await apiUnimpersonate();
        setUserData(response);
        roleChange();
        resolve(response);
      } catch (error) {
        console.log("[useAuth hook] Unimpersonate ERROR !");
        console.log(error);
        logout();
        reject(error);
      }
      setIsLoading(false);
    });
    return ret;
  };

  const getCommitteeMembership = (
    type: number,
    location?: number
  ): CommitteeMember[] | undefined => {
    var result = undefined;
    switch (type) {
      case CommitteeTypeEnum.Institution:
        result = currentRole?.committeeMemberships.filter(
          (v) =>
            v.committeeType === CommitteeTypeEnum.Institution &&
            v.institutionId === location
        );
        break;
      case CommitteeTypeEnum.County:
        result = currentRole?.committeeMemberships.filter(
          (v) =>
            v.committeeType === CommitteeTypeEnum.County &&
            v.countyId === location
        );
        break;
      case CommitteeTypeEnum.Ministry:
        result = currentRole?.committeeMemberships.filter(
          (v) =>
            v.committeeType === CommitteeTypeEnum.Ministry &&
            !v.institutionId &&
            !v.countyId
        );
        break;
    }
    return result;
  };
  const getCommitteeMembershipByCommitteeId = (
    id?: number
  ): CommitteeMember[] | undefined => {
    if (!id) return;

    const result = currentRole?.committeeMemberships.filter(
      (v) => v.committeeId === id
    );
    return result;
  };
  const getRegionalCommitteeMembershipByInstitutionId = (
    institutionId: number
  ): CommitteeMember[] | undefined => {
    const result = currentRole?.committeeMemberships.filter(
      (v) =>
        v.committeeType === CommitteeTypeEnum.RegionalCommittee &&
        v.regionalCommitteeInstitutionIds?.some((y) => y === institutionId)
    );
    return result;
  };

  // Function used to check if user has access to a certain role
  const hasRoleAccess = (access: RoleAccess): boolean => {
    if (!currentRole?.role) return false;

    let hasAccess = true;

    if (access.whitelist && access.whitelist.length > 0) {
      hasAccess = access.whitelist.includes(currentRole.role);
    }

    if (access.blacklist && access.blacklist.length > 0 && hasAccess) {
      hasAccess = !access.blacklist.includes(currentRole.role);
    }

    return hasAccess;
  };

  return {
    isLoading,
    authenticated,
    user,
    currentRole,
    roles,
    userInfo,
    login,
    logout,
    roleChange,
    hasPermission,
    impersonate,
    unimpersonate,
    getCommitteeMembership,
    getCommitteeMembershipByCommitteeId,
    getRegionalCommitteeMembershipByInstitutionId,
    hasRoleAccess,
  };
}
